import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowRight, 
  BarChart3, 
  Users, 
  Zap, 
  Trophy
} from 'lucide-react';

// Rotating Unsplash backgrounds - Young KIDS (ages 6-12) doing sports
const backgrounds = [
  'https://images.unsplash.com/photo-1622659099549-5eda1203b849?w=1920&q=80', // Young girl actively playing soccer
  'https://images.unsplash.com/photo-1490326149782-dd42fa59bd9f?w=1920&q=80', // Little League baseball batter in action
  'https://images.unsplash.com/photo-1574744918163-6cef6f4a31b0?w=1920&q=80', // Young girl swimming with goggles
  'https://images.unsplash.com/photo-1765939928892-954004cbeec5?w=1920&q=80', // Young ballet dancer in graceful pose
];

const AuthPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentBg, setCurrentBg] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Rotate backgrounds every 10 seconds (slower, more relaxed)
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentBg((prev) => (prev + 1) % backgrounds.length);
        setIsTransitioning(false);
      }, 500);
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: BarChart3,
      text: "Daily & weekly summaries",
      color: "text-emerald-400"
    },
    {
      icon: Users,
      text: "Multi-kid chaos → organized!",
      color: "text-pink-400"
    },
    {
      icon: Zap,
      text: "Never miss a practice again",
      color: "text-yellow-400"
    },
    {
      icon: Trophy,
      text: "Create cool stats for your kids",
      color: "text-amber-400"
    }
  ];

  return (
    <div className="h-screen relative overflow-hidden">
      {/* Rotating Background Images */}
      {backgrounds.map((bg, index) => (
        <div
          key={index}
          className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${
            index === currentBg ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ backgroundImage: `url('${bg}')` }}
        />
      ))}
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-purple-900/70 to-indigo-900/80" />
      
      {/* Content */}
      <div className="relative z-10 h-full flex flex-col">
        {/* Header */}
        <header className="px-6 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_kidplay-hub/artifacts/7zuwi818_jgzjhpdo_image.png"
              alt="SmartDaddy"
              className="h-8 md:h-10 w-auto"
            />
            <span className="px-2 py-0.5 bg-purple-600 text-white text-[10px] font-bold rounded-full">
              BETA
            </span>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/handler/sign-in')}
              className="text-white hover:bg-white/10"
            >
              Sign In
            </Button>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-6 py-4">
          <div className="max-w-2xl text-center text-white">
            {/* Headline */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3 leading-tight">
              Are Your Kid's{' '}
              <span className="text-yellow-400">schedules</span>
              {' '}driving you{' '}
              <span className="text-yellow-400">crazy?</span>
            </h1>
            
            <p className="text-lg md:text-xl mb-6 text-white/90">
              Level up your dad skills with scheduling —{' '}
              <span className="text-blue-400 font-semibold">Powered by AI</span>
            </p>

            {/* CTA Button */}
            <Button
              onClick={() => navigate('/handler/sign-up')}
              size="lg"
              className="h-12 px-8 text-base rounded-full bg-gradient-to-r from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800 shadow-2xl shadow-purple-900/50"
            >
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>

            <p className="mt-2 text-sm text-white/60">
              Free to use • No credit card required
            </p>

            {/* Features */}
            <div className="grid grid-cols-2 gap-3 mt-6 max-w-xl mx-auto">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg p-3 text-left"
                >
                  <div className={`p-1.5 rounded-md bg-white/20 ${feature.color}`}>
                    <feature.icon className="h-4 w-4" />
                  </div>
                  <span className="text-white/90 text-xs font-medium">{feature.text}</span>
                </div>
              ))}
            </div>

            {/* Testimonial */}
            <div className="mt-6 bg-white/10 backdrop-blur-sm rounded-lg p-4 max-w-md mx-auto">
              <p className="text-base italic text-white/90">
                "Finally, an app that gets it." 🙌😅
              </p>
              <p className="text-xs text-white/60 mt-1">
                — Exhausted dads everywhere
              </p>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="px-6 py-3 text-center shrink-0">
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-1 text-xs text-white/50">
            <span>© {new Date().getFullYear()} SmartDaddy AI</span>
            <Link to="/privacy" className="hover:text-white/80">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white/80">Terms of Use</Link>
            <Link to="/contact" className="hover:text-white/80">Contact Us</Link>
            <Link to="/pricing" className="hover:text-white/80">Pricing</Link>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default AuthPage;
